import type { ReactNode } from 'react';
import {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { ChassisTraceEdge, ChassisTracePoint, ChassisTraceRoom } from './traceGeometry';
import { colors, darkThemeSelector, styled } from '../../../stitches.config';
import { slotVars } from '../../Slots/atoms/utils';
import { tracePath, traceRoomMargin, unboundedRoom } from './traceGeometry';

export type { ChassisTraceEdge } from './traceGeometry';

export type ChassisTraceVariant =
  | 'alternative'
  | 'attention'
  | 'brand'
  | 'negative'
  | 'neutral'
  | 'positive';

/**
 * A line between two chips on the same chassis.
 *
 * Endpoints are the `traceId`s the chips were given, not port numbers: a dual switch numbers both
 * halves from 1, so a number can't say which unit it means. Anything stable and unique per chip
 * works — the dashboard uses the interface UUID.
 */
export type ChassisTrace = {
  /** `traceId` of the chip the line starts at. */
  from: string;
  /** `traceId` of the chip it ends at. */
  to: string;
  variant?: ChassisTraceVariant;
};

type ChassisTraceAnchor = {
  node: HTMLElement;
  edge: ChassisTraceEdge;
  muted: boolean;
};

/** Tracked apart so neither cancels the other: leaving a chip hands the highlight back to focus. */
type ChassisTraceHighlightSource = 'focus' | 'hover';

type ChassisTraceRegistry = {
  register: (traceId: string, anchor: ChassisTraceAnchor) => () => void;
  highlight: (source: ChassisTraceHighlightSource, traceId: string | null) => void;
};

const ChassisTraceContext = createContext<ChassisTraceRegistry | null>(null);

const noHighlight: ReadonlySet<string> = new Set();

/** The `traceId`s at either end of the line currently being followed. */
const ChassisTraceHighlightContext = createContext<ReadonlySet<string>>(noHighlight);

/**
 * Registers a chip as one end of a line, and hands back the ref to put on it.
 *
 * A no-op ref outside a `Chassis` that was given traces, so a chip carrying a `traceId` on a
 * faceplate that draws none costs nothing.
 *
 * `muted` is whether the chip's slot is muted: a line fades as soon as either of its chips does,
 * so it never reads brighter than what it lands on.
 */
export function useChassisTraceAnchor(
  traceId: string | undefined,
  edge: ChassisTraceEdge,
  muted = false,
) {
  const registry = useContext(ChassisTraceContext);

  return useCallback(
    (node: HTMLElement | null) => {
      if (!registry || traceId === undefined || node === null) return undefined;
      return registry.register(traceId, { node, edge, muted });
    },
    [registry, traceId, edge, muted],
  );
}

/** Props that make a slot pick out its own line. They go on the slot, not the chip: bigger target. */
export function useChassisTraceHighlight(traceId: string | undefined) {
  const registry = useContext(ChassisTraceContext);

  return useMemo(() => {
    if (!registry || traceId === undefined) return undefined;

    return {
      onPointerEnter: () => registry.highlight('hover', traceId),
      onPointerLeave: () => registry.highlight('hover', null),
      onFocus: () => registry.highlight('focus', traceId),
      onBlur: () => registry.highlight('focus', null),
    };
  }, [registry, traceId]);
}

/** Whether this chip is one end of the line being followed, and so has to come up over it. */
export function useChassisTraceHighlighted(traceId: string | undefined) {
  const highlighted = useContext(ChassisTraceHighlightContext);
  return traceId !== undefined && highlighted.has(traceId);
}

/** Not a `<marker>`: only `context-stroke` would take the line's colour, and support is patchy. */
const traceArrowPath = 'M -4 -3 L 0 0 L -4 3';

type ChassisTraceEmphasis = 'muted' | 'raised';

const ChassisTracePath = styled('path', {
  fill: 'none',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  vectorEffect: 'non-scaling-stroke',
  transition: 'opacity 0.1s ease-in-out',

  variants: {
    /** A line whose ends the table has filtered out, fading in step with its slots. */
    dimmed: {
      true: {
        opacity: slotVars.mutedOpacity,
      },
      false: {},
    },
    /** How a line reads while another is being followed. */
    emphasis: {
      raised: {
        strokeWidth: 3,
      },
      muted: {
        opacity: slotVars.mutedOpacity,
      },
    },
    variant: {
      alternative: {
        stroke: colors.strokeAlternativeLight,

        [darkThemeSelector]: { stroke: colors.strokeAlternativeDark },
      },
      attention: {
        stroke: colors.strokeAttentionLight,

        [darkThemeSelector]: { stroke: colors.strokeAttentionDark },
      },
      brand: {
        stroke: colors.strokeBrandLight,

        [darkThemeSelector]: { stroke: colors.strokeBrandDark },
      },
      negative: {
        stroke: colors.strokeNegativeLight,

        [darkThemeSelector]: { stroke: colors.strokeNegativeDark },
      },
      // The hardware neutral, not the chrome one: at 1.3:1 on a white card that is a divider weight,
      // and is what a chip's tail fades into. Matches `Port` and `Outlet`'s uncoloured stroke.
      neutral: {
        stroke: colors.hardwareStrokeNeutralLight,

        [darkThemeSelector]: { stroke: colors.hardwareStrokeNeutralDark },
      },
      positive: {
        stroke: colors.strokePositiveLight,

        [darkThemeSelector]: { stroke: colors.strokePositiveDark },
      },
    },
  },
});
ChassisTracePath.displayName = 'ChassisTracePath';

/** The band of card colour a raised line carries under it, so it stays one line over the glyphs. */
const ChassisTraceCasing = styled('path', {
  fill: 'none',
  stroke: colors.bgApplicationLight,
  strokeWidth: 6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  vectorEffect: 'non-scaling-stroke',

  [darkThemeSelector]: {
    stroke: colors.bgApplicationDark,
  },

  variants: {
    /** Translucent like the dimmed chips around it, not a solid band across a faded card. */
    dimmed: {
      true: {
        opacity: slotVars.mutedOpacity,
      },
      false: {},
    },
  },
});
ChassisTraceCasing.displayName = 'ChassisTraceCasing';

/**
 * The plane the lines draw on: the chassis card's own box, with no `viewBox` so one SVG unit is one
 * CSS pixel and measured coordinates can be used as they come.
 *
 * `overflow: visible` because the chips it joins are not all inside the card — one above a top row
 * reaches over the group bracket and one below a bottom row past the card's edge, and a line ending
 * at either would otherwise be clipped. No `zIndex` by default, so it paints under everything a slot
 * draws (`slotVars.zIndex` starts at 1) and a line reads as passing behind the ports.
 */
const ChassisTraceSurface = styled('svg', {
  position: 'absolute',
  inset: 0,
  overflow: 'visible',
  pointerEvents: 'none',

  variants: {
    /**
     * Carries just the followed line out from behind the ports, on a plane of its own — an SVG is
     * one layer, so raising the resting plane would bring the sibling lines up over their own chips.
     */
    raised: {
      true: {
        zIndex: slotVars.zIndex.traceRaised,
      },
      false: {},
    },
  },
});
ChassisTraceSurface.displayName = 'ChassisTraceSurface';

function samePoints(a: Map<string, ChassisTracePoint>, b: Map<string, ChassisTracePoint>) {
  if (a.size !== b.size) return false;

  for (const [id, point] of a) {
    const other = b.get(id);
    if (
      !other ||
      other.x !== point.x ||
      other.y !== point.y ||
      other.edge !== point.edge ||
      other.field !== point.field ||
      other.muted !== point.muted
    ) {
      return false;
    }
  }

  return true;
}

/** Nearest ancestor that would cut a line off, or none where the diagram runs to the viewport. */
function clippingParent(node: Element): HTMLElement | null {
  for (let parent = node.parentElement; parent; parent = parent.parentElement) {
    if (getComputedStyle(parent).overflowY !== 'visible') return parent;
  }

  return null;
}

export type ChassisTracesProps = {
  children: ReactNode;
  traces?: ChassisTrace[];
};

/**
 * Draws lines between chips on one chassis, and provides the registry they anchor to.
 *
 * Renders its children untouched and the lines behind them — the one being followed comes up over
 * them, see `ChassisTraceSurface` — so it wraps the units inside the card's own positioned box rather
 * than replacing anything.
 *
 * Position has to be measured rather than derived. A faceplate is laid out by CSS — sections,
 * groups, rows, gaps of arbitrary width, and for a pair, two whole units side by side — so where a
 * given port lands is not a function of its number. With no traces to draw nothing is measured and
 * no observer is created, which is every faceplate that isn't showing a SPAN session.
 */
export function ChassisTraces({ children, traces }: ChassisTracesProps) {
  const surfaceRef = useRef<SVGSVGElement>(null);
  const anchorsRef = useRef(new Map<string, ChassisTraceAnchor>());
  const [points, setPoints] = useState<Map<string, ChassisTracePoint>>(new Map());
  const [room, setRoom] = useState<ChassisTraceRoom>(unboundedRoom);
  /** Bumped when the set of anchors changes, which is what re-runs the measurement below. */
  const [generation, setGeneration] = useState(0);
  const [hovered, setHovered] = useState<string | null>(null);
  const [focused, setFocused] = useState<string | null>(null);

  const registry = useMemo<ChassisTraceRegistry>(
    () => ({
      register: (traceId, anchor) => {
        anchorsRef.current.set(traceId, anchor);
        setGeneration((previous) => previous + 1);

        return () => {
          anchorsRef.current.delete(traceId);
          setGeneration((previous) => previous + 1);
        };
      },
      highlight: (source, traceId) => {
        (source === 'hover' ? setHovered : setFocused)(traceId);
      },
    }),
    [],
  );

  const isDrawing = (traces?.length ?? 0) > 0;

  /*
   * Measured in a layout effect so the lines are in place before the browser paints — computing
   * them in a passive effect draws the faceplate once without them and once with, which on a
   * 48-port switch is a visible flash of unconnected chips.
   *
   * Everything is measured against the surface's own box, which is the card's, so the numbers are
   * already in the coordinate system the paths are drawn in. The observer watches the chips as well
   * as the card: the properties panel animates its width over 0.3s and reflows the diagram under
   * them without the card itself changing size.
   */
  useLayoutEffect(() => {
    const surface = surfaceRef.current;
    if (!surface || !isDrawing) return undefined;

    const anchors = anchorsRef.current;
    const clip = clippingParent(surface);

    const measure = () => {
      const origin = surface.getBoundingClientRect();
      const next = new Map<string, ChassisTracePoint>();

      let nextRoom = unboundedRoom;
      if (clip) {
        // The padding box: what is on screen, rather than what the element takes up.
        const top = clip.getBoundingClientRect().top + clip.clientTop - origin.top;

        nextRoom = {
          top: top + traceRoomMargin,
          bottom: top + clip.clientHeight - traceRoomMargin,
        };
      }

      for (const [traceId, anchor] of anchors) {
        // A line aims at the pill, but the tail's far end — the slot's own edge — is the chip box's.
        // The pill is the first child on either edge: `bottom` reverses the flex, not the DOM.
        const pill = (anchor.node.firstElementChild ?? anchor.node).getBoundingClientRect();
        const chip = anchor.node.getBoundingClientRect();
        const away = anchor.edge === 'top' ? -1 : 1;
        const slot = (anchor.edge === 'top' ? chip.bottom : chip.top) - origin.top;

        next.set(traceId, {
          x: pill.left - origin.left + pill.width / 2,
          // The pill's lane-facing edge, not its centre: a muted slot's pill is translucent, and a
          // line running on under it reads as chopped off. Every curve arrives from the lane side.
          y: (anchor.edge === 'top' ? pill.top : pill.bottom) - origin.top,
          edge: anchor.edge,
          field: slot + away * slotVars.chipLane.far,
          muted: anchor.muted,
        });
      }

      // Guarded because the observer fires once per element on `observe`, and an unguarded set
      // would re-render the whole faceplate once per chip at mount for no change.
      setPoints((previous) => (samePoints(previous, next) ? previous : next));
      setRoom((previous) =>
        previous.top === nextRoom.top && previous.bottom === nextRoom.bottom ? previous : nextRoom,
      );
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(surface);
    if (clip) observer.observe(clip);
    for (const anchor of anchors.values()) {
      observer.observe(anchor.node);
    }

    return () => observer.disconnect();
  }, [generation, isDrawing]);

  /** Whichever the pointer is on, and what has focus once it moves off. */
  const highlightedTraceId = hovered ?? focused;

  /** Both ends of every line the highlighted chip is on, so those chips come up over the plane. */
  const highlighted = useMemo(() => {
    if (highlightedTraceId === null) return noHighlight;

    const ends = new Set([highlightedTraceId]);
    for (const trace of traces ?? []) {
      if (trace.from === highlightedTraceId) ends.add(trace.to);
      if (trace.to === highlightedTraceId) ends.add(trace.from);
    }

    return ends;
  }, [highlightedTraceId, traces]);

  const paths = useMemo(() => {
    if (!traces?.length) return [];

    return traces.flatMap((trace) => {
      const from = points.get(trace.from);
      const to = points.get(trace.to);
      // A trace naming a chip this faceplate doesn't draw is skipped rather than guessed at — a
      // session can name a port on a switch that isn't the one being looked at.
      if (!from || !to) return [];

      const { d, arrow } = tracePath(from, to, room);

      return [
        {
          key: `${trace.from}-${trace.to}`,
          d,
          arrow,
          ends: [trace.from, trace.to],
          variant: trace.variant ?? ('neutral' as const),
          // Either end: a line must never read brighter than a chip it lands on.
          dimmed: Boolean(from.muted || to.muted),
        },
      ];
    });
  }, [points, room, traces]);

  const isFollowed = (ends: string[]) =>
    highlightedTraceId !== null && ends.includes(highlightedTraceId);
  const resting = paths.filter((path) => !isFollowed(path.ends));
  const followed = paths.filter((path) => isFollowed(path.ends));

  const drawTrace = (path: (typeof paths)[number], emphasis: ChassisTraceEmphasis | undefined) => (
    <Fragment key={path.key}>
      <ChassisTracePath
        d={path.d}
        dimmed={path.dimmed}
        emphasis={emphasis}
        variant={path.variant}
      />
      <ChassisTracePath
        d={traceArrowPath}
        transform={`translate(${path.arrow.x} ${path.arrow.y}) rotate(${path.arrow.angle})`}
        dimmed={path.dimmed}
        emphasis={emphasis}
        variant={path.variant}
      />
    </Fragment>
  );

  return (
    <ChassisTraceContext.Provider value={registry}>
      <ChassisTraceHighlightContext.Provider value={highlighted}>
        {children}
        {isDrawing && (
          // Decorative: every line restates what the chips it joins already spell out.
          <ChassisTraceSurface ref={surfaceRef} aria-hidden focusable={false}>
            {resting.map((path) =>
              drawTrace(path, highlightedTraceId === null ? undefined : 'muted'),
            )}
          </ChassisTraceSurface>
        )}
        {followed.length > 0 && (
          <ChassisTraceSurface raised aria-hidden focusable={false}>
            {/*
              Every casing ahead of every line, so several raised lines converging on one chip read
              as crossing rather than as cutting gaps in each other.
            */}
            {followed.map((path) => (
              <ChassisTraceCasing key={`${path.key}-casing`} d={path.d} dimmed={path.dimmed} />
            ))}
            {followed.map((path) => drawTrace(path, 'raised'))}
          </ChassisTraceSurface>
        )}
      </ChassisTraceHighlightContext.Provider>
    </ChassisTraceContext.Provider>
  );
}
